// Toptan göçme analizi giriş noktası: duvar girdisi → zemin modeli → statik (ve kh ile depremli) kritik daire.
// `analyzeWall`den bağımsızdır; web tarafı Web Worker'da çağırır, rapor modeli senkron çağırabilir.
import { deg2rad } from "../math/angles";
import { fmt } from "../format/number";
import { parametricToPolygon } from "../geometry/parametric";
import { rcToPolygon } from "../geometry/rcCantilever";
import { buildSection } from "../geometry/section";
import { computeSeismic } from "../seismic/coefficients";
import { WallInputSchema, type WallInput } from "../types/input";
import type { GlobalStabilityAnalysis, GlobalStabilityResult, SlopeMethod, SlopeSlice } from "../types/result";
import { factorOfSafety } from "./bishop";
import { wallGroundModel, type GroundModel } from "./model";
import { gridCounts, searchCritical, type SearchOptions } from "./search";
import { allocSlices, buildSlices } from "./slices";

export { wallGroundModel, homogeneousSlopeModel, polylineY, WALL_C, type GroundModel, type HomogeneousSlope } from "./model";
export { searchCritical, circleFs, gridCounts, type SearchOptions, type SearchBest } from "./search";
export { bishopFs, felleniusFs, factorOfSafety, type FsResult } from "./bishop";
export { allocSlices, buildSlices, type SliceArrays } from "./slices";

export interface GlobalStabilityOptions {
  /** kh'yi dışarıdan ver (deprem girdisinden türetmek yerine) */
  kh?: number;
}

const MAT: SlopeSlice["material"][] = ["backfill", "foundation", "wall"];

/** Kritik dairenin dilim tablosu (çizim/rapor) */
function sliceTable(model: GroundModel, best: { circle: GlobalStabilityResult["circle"] }, n: number): SlopeSlice[] {
  const arr = allocSlices(n);
  if (!buildSlices(model, best.circle, arr)) return [];
  const out: SlopeSlice[] = [];
  for (let i = 0; i < n; i++) {
    out.push({ x: arr.x[i]!, yBase: arr.yb[i]!, ySurf: arr.ys[i]!, width: arr.b, W: arr.W[i]!, alphaDeg: (Math.asin(arr.sinA[i]!) * 180) / Math.PI,
      phiDeg: (Math.atan(arr.tanPhi[i]!) * 180) / Math.PI, c: arr.c[i]!, u: arr.u[i]!, material: MAT[arr.mat[i]!] ?? "foundation" });
  }
  return out;
}

const METHOD_LABEL: Record<SlopeMethod, string> = { bishop: "Bishop basitleştirilmiş", fellenius: "Fellenius" };

export function analyzeGlobalStability(rawInput: WallInput, options: GlobalStabilityOptions = {}): GlobalStabilityAnalysis {
  const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
  const input = WallInputSchema.parse(rawInput);
  const gs = input.globalStability;
  const beta = deg2rad(input.backfill.slopeAngle);
  const polygon = input.geometry.mode === "parametric" ? parametricToPolygon(input.geometry) : input.geometry.mode === "rc-cantilever" ? rcToPolygon(input.geometry) : input.geometry.points;
  const section = buildSection(polygon, beta);
  const model = wallGroundModel(input, section);

  const H = section.yTop - section.baseY;
  const yF = section.toe.y + input.geometry.embedmentDepth;
  const grid = gs.searchGrid.auto
    ? { xRange: [section.toe.x - 0.5 * H, section.heel.x + 2 * H] as [number, number], yRange: [section.yTop + 0.25 * H, section.yTop + 3 * H] as [number, number], step: Math.max(0.1, H / 6) }
    : { xRange: [Math.min(...gs.searchGrid.xRange), Math.max(...gs.searchGrid.xRange)] as [number, number], yRange: [Math.min(...gs.searchGrid.yRange), Math.max(...gs.searchGrid.yRange)] as [number, number], step: gs.searchGrid.step };
  const radiusStep = gs.radiusStep ?? Math.max(0.05, H / 8);
  const exitLimitX = section.toe.x - 2.5 * H;
  const radii = (cx: number, cy: number): [number, number] => [cy - (section.baseY - gs.minDepthBelowBase), Math.hypot(cx - exitLimitX, cy - yF)];
  const base: Omit<SearchOptions, "kh"> = { method: gs.method, slices: gs.slices, ...grid, radiusStep, radii };

  const run = (caseId: "static" | "seismic", kh: number, target: number): GlobalStabilityResult => {
    const best = searchCritical(model, { ...base, kh });
    if (!best) throw new Error("Toptan göçme: tarama ızgarasında geçerli kayma dairesi bulunamadı (ızgara aralığını genişletin)");
    const arr = allocSlices(gs.slices);
    buildSlices(model, best.circle, arr);
    const fsr = factorOfSafety(arr, { method: gs.method, kh, cy: best.circle.cy, r: best.circle.r });
    const ok = best.fs >= target;
    return {
      caseId, method: gs.method, kh, fsMin: best.fs, circle: best.circle,
      exit: { x: arr.x1, y: model.surfaceY(arr.x1) }, entry: { x: arr.x2, y: model.surfaceY(arr.x2) },
      slices: sliceTable(model, best, gs.slices), driving: fsr.driving, resisting: fsr.resisting,
      target, ok, searched: best.searched, iterations: fsr.iterations,
      verdict: `${ok ? "SAĞLIYOR" : "SAĞLAMIYOR"} — GK = ${fmt(best.fs, 2)} ${ok ? "≥" : "<"} ${fmt(target, 2)} (${METHOD_LABEL[gs.method]}${kh > 0 ? `, kh = ${fmt(kh, 3)}` : ""})`,
    };
  };

  const st = run("static", 0, gs.targets.static);
  let seismic: GlobalStabilityResult | undefined;
  let kh = options.kh;
  if (kh === undefined && gs.seismic === "pseudo-static" && input.seismic.enabled) kh = computeSeismic(input.seismic, () => undefined).kh;
  if (kh !== undefined && kh > 0 && gs.seismic === "pseudo-static") seismic = run("seismic", kh, gs.targets.seismic);

  const { nx, ny } = gridCounts(grid);
  const t1 = typeof performance !== "undefined" ? performance.now() : Date.now();
  return {
    static: st, ...(seismic ? { seismic } : {}), method: gs.method,
    grid: { ...grid, nx, ny }, radiusStep, minDepthBelowBase: gs.minDepthBelowBase, slices: gs.slices,
    elapsedMs: t1 - t0, ok: st.ok && (seismic?.ok ?? true),
  };
}
